/**
 * draftStateMachine.blockAuthoring.ts — Block Authoring Palette
 * placement state machines (Phase K.2 / K.3).
 *
 * Hosts the multi-click placement flows that begin when the user clicks a
 * palette item in the Block Authoring shell. Each flow ends by dispatching
 * a `geometry/add-dynamic-parameter` (or `add-dynamic-action`) command so
 * the existing DYN-1.7 reducer pipeline records the new authoring object
 * verbatim — no parallel command surface.
 *
 * Phase K.2a — Linear parameter (BPARAM-LINEAR), 3-click flow:
 *   1. param-linear-base   — pick base/start point (basePoint).
 *   2. param-linear-end    — pick end point. The vector (end - base) defines
 *                            the linear axis. Distance = |end - base|.
 *   3. param-linear-label  — pick label position. We then commit a
 *                            `geometry/add-dynamic-parameter` with the
 *                            full LinearParameter shape (basePoint /
 *                            secondPoint / defaultDistance / currentDistance
 *                            / valueSet / id / name / label / description).
 *
 * Phase K.2b — remaining 9 parameter placements. Each follows the same
 * shape (return `nextSession` to advance, or `nextSession: null` + `commit`
 * to dispatch and end). The typed parameter shape per `kind` matches
 * `dynamicBlockParameters.ts`:
 *
 *   Single-click placements (drop a glyph at the clicked point):
 *     BPARAM-POINT       → param-point-place       → pointParameter
 *     BPARAM-BASEPOINT   → param-basepoint-place   → basePointParameter
 *     BPARAM-VISIBILITY  → param-visibility-place  → visibilityParameter
 *                                                    (with default State1)
 *     BPARAM-LOOKUP      → param-lookup-place      → lookupParameter +
 *                          create-lookup-table (multi-command commit)
 *
 *   Three-click placements (base → end → label, mirroring Linear):
 *     BPARAM-FLIP        → axisStart/axisEnd       → flipParameter
 *     BPARAM-POLAR       → basePoint/secondPoint   → polarParameter
 *                                                    (defaultDistance +
 *                                                    defaultAngle from
 *                                                    end - base)
 *     BPARAM-XY          → basePoint/secondPoint   → xyParameter
 *                                                    (defaultX, defaultY
 *                                                    from end - base)
 *     BPARAM-ROTATION    → basePoint/radius end    → rotationParameter
 *                                                    (radius = |end - base|,
 *                                                    defaultAngle = atan2)
 *
 *   Two-click placement (position → direction):
 *     BPARAM-ALIGNMENT   → position + dir-end      → alignmentParameter
 *                                                    (defaultAngle from
 *                                                    direction vector)
 *
 * The blockName is read from session.options.blockName (set by App.tsx
 * when starting the BPARAM-* session); the reducer
 * (`dynamicAuthoringReducerHelpers.handleAddDynamicParameter`) gates on
 * the active BEDIT space matching the blockName.
 *
 * Returns `null` when the step is not a block-authoring placement step,
 * letting the caller fall through to its own switch.
 */

import type {
  AlignmentParameter,
  ArrayAction,
  BasePointParameter,
  DynamicAction,
  FlipAction,
  FlipParameter,
  LinearParameter,
  LookupAction,
  LookupParameter,
  LookupTable,
  MoveAction,
  PointParameter,
  PolarParameter,
  PolarStretchAction,
  RotateAction,
  RotationParameter,
  ScaleAction,
  StretchAction,
  VisibilityParameter,
  XYParameter,
} from "@dxf-ai-editor/drawing-model";
import type { EditorCommand, EntityPreview } from "@dxf-ai-editor/contracts";

import { t } from "../i18n";
import { DRAFT_POINT_PICK_TOLERANCE } from "./draftTypes";
import type {
  DraftPoint,
  DraftSession,
  DraftStep,
  DraftCommitResult,
} from "./draftTypes";
import { resolveEntityPreviewCoordinate } from "./draftSharedHelpers";

/**
 * True when the step belongs to a block-authoring placement flow. Used by
 * the main state machine to early-route into this module before the big
 * switch.
 */
export function isBlockAuthoringStep(step: DraftStep): boolean {
  return (
    // Phase K.2a — Linear
    step === "param-linear-base" ||
    step === "param-linear-end" ||
    step === "param-linear-label" ||
    // Phase K.2b — remaining 9
    step === "param-point-place" ||
    step === "param-basepoint-place" ||
    step === "param-visibility-place" ||
    step === "param-lookup-place" ||
    step === "param-flip-base" ||
    step === "param-flip-end" ||
    step === "param-flip-label" ||
    step === "param-polar-base" ||
    step === "param-polar-end" ||
    step === "param-polar-label" ||
    step === "param-xy-base" ||
    step === "param-xy-end" ||
    step === "param-xy-label" ||
    step === "param-rotation-base" ||
    step === "param-rotation-radius-end" ||
    step === "param-rotation-label" ||
    step === "param-alignment-base" ||
    step === "param-alignment-end" ||
    // Phase K.3 — action placement steps.
    step === "action-move-pick-parameter" ||
    step === "action-move-select-objects" ||
    step === "action-move-place" ||
    step === "action-scale-pick-parameter" ||
    step === "action-scale-select-objects" ||
    step === "action-scale-place" ||
    step === "action-stretch-pick-parameter" ||
    step === "action-stretch-select-objects" ||
    step === "action-stretch-frame-corner-1" ||
    step === "action-stretch-frame-corner-2" ||
    step === "action-stretch-place" ||
    step === "action-polar-stretch-pick-parameter" ||
    step === "action-polar-stretch-select-objects" ||
    step === "action-polar-stretch-frame-corner-1" ||
    step === "action-polar-stretch-frame-corner-2" ||
    step === "action-polar-stretch-place" ||
    step === "action-rotation-pick-parameter" ||
    step === "action-rotation-select-objects" ||
    step === "action-rotation-place" ||
    step === "action-flip-pick-parameter" ||
    step === "action-flip-select-objects" ||
    step === "action-flip-place" ||
    step === "action-array-pick-parameter" ||
    step === "action-array-select-objects" ||
    step === "action-array-place" ||
    step === "action-lookup-pick-parameter" ||
    step === "action-lookup-select-objects" ||
    step === "action-lookup-place"
  );
}

/**
 * True when the step is one of the action placement select-objects steps.
 * Used by the text handler to know that an empty Enter should advance to
 * the next step (frame-corner-1 for stretch / polar-stretch, place for
 * the rest) rather than cancel the session.
 */
export function isActionSelectObjectsStep(step: DraftStep): boolean {
  return (
    step === "action-move-select-objects" ||
    step === "action-scale-select-objects" ||
    step === "action-stretch-select-objects" ||
    step === "action-polar-stretch-select-objects" ||
    step === "action-rotation-select-objects" ||
    step === "action-flip-select-objects" ||
    step === "action-array-select-objects" ||
    step === "action-lookup-select-objects"
  );
}

/**
 * Compute a parameter id that does not collide with any existing
 * parameter in the active block. The reducer enforces uniqueness; we
 * provide the deterministic default that matches BlockAuthoringPalette's
 * `nextParameterId()` pattern.
 *
 * Caller passes the existing parameter ids serialised as JSON in
 * `session.options.existingParameterIds` (see App.tsx onBeginPlace).
 */
function nextParameterId(session: DraftSession): string {
  const raw = session.options.existingParameterIds ?? "[]";
  let used: string[] = [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      used = parsed.filter((value): value is string => typeof value === "string");
    }
  } catch {
    used = [];
  }
  const usedSet = new Set(used);
  let i = used.length + 1;
  while (usedSet.has(`param-${i}`)) i += 1;
  return `param-${i}`;
}

/**
 * Compute a parameter name that does not collide with any existing
 * parameter name. Mirrors BlockAuthoringPalette.startAddLinear's
 * `nextParameterName("Distance")`. Caller passes existing names via
 * `session.options.existingParameterNames` (JSON-serialised string[]).
 */
function nextParameterName(session: DraftSession, prefix: string): string {
  const raw = session.options.existingParameterNames ?? "[]";
  let used: string[] = [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      used = parsed.filter((value): value is string => typeof value === "string");
    }
  } catch {
    used = [];
  }
  const usedSet = new Set(used);
  let i = 1;
  while (usedSet.has(`${prefix}${i}`)) i += 1;
  return `${prefix}${i}`;
}

/**
 * Compute a fresh lookup-table id / name (BPARAM-LOOKUP only). Same
 * collision-free strategy as parameter ids, but seeded from
 * `session.options.existingLookupTableIds` /
 * `session.options.existingLookupTableNames`.
 */
function nextLookupTableId(session: DraftSession): string {
  const raw = session.options.existingLookupTableIds ?? "[]";
  let used: string[] = [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      used = parsed.filter((value): value is string => typeof value === "string");
    }
  } catch {
    used = [];
  }
  const usedSet = new Set(used);
  let i = used.length + 1;
  while (usedSet.has(`lookup_table-${i}`)) i += 1;
  return `lookup_table-${i}`;
}

function nextLookupTableName(session: DraftSession): string {
  const raw = session.options.existingLookupTableNames ?? "[]";
  let used: string[] = [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      used = parsed.filter((value): value is string => typeof value === "string");
    }
  } catch {
    used = [];
  }
  const usedSet = new Set(used);
  let i = 1;
  while (usedSet.has(`Table${i}`)) i += 1;
  return `Table${i}`;
}

function distanceBetween(a: DraftPoint, b: DraftPoint): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

function angleBetweenDeg(a: DraftPoint, b: DraftPoint): number {
  return (Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI;
}

function defaultValueSet(): { mode: "none"; increment: number; listValues: number[] } {
  return { mode: "none", increment: 0, listValues: [] };
}

// ─── Phase K.3 — Action placement helpers ──────────────────────────────

/**
 * The 8 action kinds Phase K.3 places. Mirrors `DynamicAction["kind"]`
 * from `dynamicBlockActions.ts` modulo the palette label "rotation"
 * being mapped to the schema kind "rotate".
 */
export type BlockAuthoringActionKind =
  | "move"
  | "scale"
  | "stretch"
  | "polar-stretch"
  | "rotation"
  | "flip"
  | "array"
  | "lookup";

/**
 * Action-to-parameter compatibility matrix (per
 * docs/autocad-parity/block-authoring-spec.md §1.1). Used by
 * `isCompatibleParameter` to gate the parameter-pick step.
 */
export const ACTION_COMPATIBILITY: Record<BlockAuthoringActionKind, ReadonlyArray<string>> = {
  move: ["point", "linear", "polar", "xy"],
  scale: ["linear", "polar", "xy"],
  stretch: ["linear", "polar", "xy"],
  "polar-stretch": ["polar"],
  rotation: ["rotation"],
  flip: ["flip"],
  array: ["linear", "polar", "xy"],
  lookup: ["lookup"],
};

/**
 * True when `paramKind` is allowed to drive `actionKind`. Used after the
 * user clicks a parameter glyph at the `action-{kind}-pick-parameter`
 * step. On `false`, the caller surfaces the
 * `error.baction.incompatible_parameter` toast and stays on the same
 * step (user can click another glyph or press Esc to cancel).
 */
export function isCompatibleParameter(
  actionKind: BlockAuthoringActionKind,
  paramKind: string,
): boolean {
  return ACTION_COMPATIBILITY[actionKind].includes(paramKind);
}

/**
 * Picker descriptor for a single parameter glyph — the host App.tsx
 * serializes one entry per parameter into `session.options.parameterPicks`
 * so the state machine can hit-test clicks without importing React state.
 *
 * `position` is the parameter's grip position (BasePointParameter uses
 * `position`; numeric params use `gripPosition` falling back to the
 * second/end point).
 */
export type ParameterPickDescriptor = {
  id: string;
  kind: string;
  position: { x: number; y: number };
};

/**
 * Parse the JSON-serialised parameter pick list from
 * `session.options.parameterPicks`. Returns `[]` on malformed JSON or
 * missing key — the caller will then refuse every pick (no parameter
 * available), matching AutoCAD's behaviour when a block has no
 * parameters.
 */
export function parseParameterPicks(
  raw: string | undefined,
): ParameterPickDescriptor[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (entry): entry is ParameterPickDescriptor =>
        typeof entry === "object" &&
        entry !== null &&
        typeof (entry as ParameterPickDescriptor).id === "string" &&
        typeof (entry as ParameterPickDescriptor).kind === "string" &&
        typeof (entry as ParameterPickDescriptor).position === "object" &&
        (entry as ParameterPickDescriptor).position !== null &&
        typeof (entry as ParameterPickDescriptor).position.x === "number" &&
        typeof (entry as ParameterPickDescriptor).position.y === "number",
    );
  } catch {
    return [];
  }
}

/**
 * Find the parameter glyph closest to the clicked point. Returns `null`
 * when the closest glyph is further than `tolerance` drawing units away
 * — caller stays at the pick-parameter step and waits for another click.
 *
 * Tolerance defaults to the shared loose 8-drawing-unit convention
 * (`DRAFT_POINT_PICK_TOLERANCE`); callers that know the camera zoom
 * can pass a tighter value (e.g. `8 / cameraZoom` for pixel-equivalent
 * precision).
 */
export function findParameterAtPoint(
  point: DraftPoint,
  parameters: ReadonlyArray<ParameterPickDescriptor>,
  tolerance = DRAFT_POINT_PICK_TOLERANCE,
): ParameterPickDescriptor | null {
  let best: ParameterPickDescriptor | null = null;
  let bestDist = Infinity;
  for (const param of parameters) {
    const d = Math.hypot(point.x - param.position.x, point.y - param.position.y);
    if (d < bestDist) {
      bestDist = d;
      best = param;
    }
  }
  if (!best || bestDist > tolerance) return null;
  return best;
}

/**
 * Find the entity nearest to the click point. Mirrors
 * `findClosestEntity` from `draftModifyStateMachine.ts` (used by
 * MOVE/COPY/MIRROR select steps) but kept local so this module stays
 * decoupled from the modify state machine.
 *
 * Returns the closest entity within the loose tolerance heuristic that
 * accepts ANY entity with a recognisable coordinate. Picks under the
 * same point as the previous pick are de-duped by the caller.
 */
export function findClosestEntityForActionSelect(
  point: DraftPoint,
  entities: ReadonlyArray<EntityPreview>,
): EntityPreview | null {
  let best: EntityPreview | null = null;
  let bestDist = Infinity;
  for (const entity of entities) {
    const coord = resolveEntityPreviewCoordinate(entity.properties ?? {}, point);
    if (coord) {
      const d = Math.hypot(point.x - coord.x, point.y - coord.y);
      if (d < bestDist) {
        bestDist = d;
        best = entity;
      }
    }
  }
  return best;
}

/**
 * Compute the next collision-free action id given the existing list
 * serialised into `session.options.existingActionIds`.
 */
function nextActionId(session: DraftSession): string {
  const raw = session.options.existingActionIds ?? "[]";
  let used: string[] = [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      used = parsed.filter((value): value is string => typeof value === "string");
    }
  } catch {
    used = [];
  }
  const usedSet = new Set(used);
  let i = used.length + 1;
  while (usedSet.has(`action-${i}`)) i += 1;
  return `action-${i}`;
}

/**
 * Compute the next collision-free action name with the given prefix.
 * Mirrors `BlockAuthoringPalette.nextActionName`.
 */
function nextActionName(session: DraftSession, prefix: string): string {
  const raw = session.options.existingActionNames ?? "[]";
  let used: string[] = [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      used = parsed.filter((value): value is string => typeof value === "string");
    }
  } catch {
    used = [];
  }
  const usedSet = new Set(used);
  let i = 1;
  while (usedSet.has(`${prefix}${i}`)) i += 1;
  return `${prefix}${i}`;
}

/**
 * Read the running selection-set list off the session. Identical shape
 * to draftModifyStateMachine.getTargetEntityIds — kept local so this
 * module does not import from draftModifyStateMachine.
 */
function getSelectionEntityIds(session: DraftSession): string[] {
  const raw = session.options.targetEntityIds;
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((id): id is string => typeof id === "string");
    }
  } catch {
    // fallthrough
  }
  return raw ? [raw] : [];
}

/**
 * Append `entityId` to the session's targetEntityIds option (de-dupe).
 * Returns a new DraftSession with the updated options.
 */
function appendSelectionEntityId(
  session: DraftSession,
  entityId: string,
): DraftSession {
  const existing = getSelectionEntityIds(session);
  if (existing.includes(entityId)) return session;
  const next = [...existing, entityId];
  return {
    ...session,
    options: {
      ...session.options,
      targetEntityIds: JSON.stringify(next),
    },
  };
}

/**
 * Given an action `kind` and the matching pick-parameter step, advance
 * to the action's select-objects step after recording the picked
 * parameter id. Helper avoids per-action duplicated transitions.
 */
function advanceFromPickParameter(
  session: DraftSession,
  kind: BlockAuthoringActionKind,
  parameterId: string,
): DraftSession {
  const nextStep: DraftStep =
    kind === "move"
      ? "action-move-select-objects"
      : kind === "scale"
        ? "action-scale-select-objects"
        : kind === "stretch"
          ? "action-stretch-select-objects"
          : kind === "polar-stretch"
            ? "action-polar-stretch-select-objects"
            : kind === "rotation"
              ? "action-rotation-select-objects"
              : kind === "flip"
                ? "action-flip-select-objects"
                : kind === "array"
                  ? "action-array-select-objects"
                  : "action-lookup-select-objects";
  return {
    ...session,
    step: nextStep,
    options: { ...session.options, actionParameterId: parameterId },
    hoverPoint: null,
  };
}

/**
 * Map an action's select-objects step to the *next* step after the
 * user's Enter (empty text) finalizes the selection. STRETCH and
 * POLAR-STRETCH detour through the crossing-frame; the rest go
 * straight to place.
 */
export function nextStepAfterSelectObjects(step: DraftStep): DraftStep | null {
  switch (step) {
    case "action-move-select-objects":
      return "action-move-place";
    case "action-scale-select-objects":
      return "action-scale-place";
    case "action-stretch-select-objects":
      return "action-stretch-frame-corner-1";
    case "action-polar-stretch-select-objects":
      return "action-polar-stretch-frame-corner-1";
    case "action-rotation-select-objects":
      return "action-rotation-place";
    case "action-flip-select-objects":
      return "action-flip-place";
    case "action-array-select-objects":
      return "action-array-place";
    case "action-lookup-select-objects":
      return "action-lookup-place";
    default:
      return null;
  }
}

/**
 * Build the typed `DynamicAction` payload for the place step. The
 * driving `parameterId` and `selectionEntityIds` come from session
 * options accumulated through pick-parameter and select-objects steps;
 * `crossingFrame` (when applicable) comes from the two frame-corner
 * clicks stored on `session.points`.
 */
function buildActionPayload(
  session: DraftSession,
  kind: BlockAuthoringActionKind,
): DynamicAction | null {
  const parameterId = session.options.actionParameterId ?? "";
  if (!parameterId) return null;
  const selectionEntityIds = getSelectionEntityIds(session);
  const id = nextActionId(session);
  switch (kind) {
    case "move": {
      const action: MoveAction = {
        id,
        kind: "move",
        name: nextActionName(session, "Move"),
        parameterId,
        selectionEntityIds,
        distanceMultiplier: 1,
        angleOffset: 0,
      };
      return action;
    }
    case "scale": {
      const action: ScaleAction = {
        id,
        kind: "scale",
        name: nextActionName(session, "Scale"),
        parameterId,
        selectionEntityIds,
        scaleType: "uniform",
        scaleMultiplier: 1,
      };
      return action;
    }
    case "stretch": {
      const corner1 = session.points[0];
      const corner2 = session.points[1];
      if (!corner1 || !corner2) return null;
      const action: StretchAction = {
        id,
        kind: "stretch",
        name: nextActionName(session, "Stretch"),
        parameterId,
        selectionEntityIds,
        distanceMultiplier: 1,
        angleOffset: 0,
        crossingFrame: {
          corner1: { x: corner1.x, y: corner1.y },
          corner2: { x: corner2.x, y: corner2.y },
        },
        stretchDirection: 0,
      };
      return action;
    }
    case "polar-stretch": {
      const corner1 = session.points[0];
      const corner2 = session.points[1];
      if (!corner1 || !corner2) return null;
      const action: PolarStretchAction = {
        id,
        kind: "polar-stretch",
        name: nextActionName(session, "PolarStretch"),
        parameterId,
        selectionEntityIds,
        distanceMultiplier: 1,
        angleOffset: 0,
        crossingFrame: {
          corner1: { x: corner1.x, y: corner1.y },
          corner2: { x: corner2.x, y: corner2.y },
        },
      };
      return action;
    }
    case "rotation": {
      const action: RotateAction = {
        id,
        kind: "rotate",
        name: nextActionName(session, "Rotate"),
        parameterId,
        selectionEntityIds,
        angleMultiplier: 1,
      };
      return action;
    }
    case "flip": {
      const action: FlipAction = {
        id,
        kind: "flip",
        name: nextActionName(session, "Flip"),
        parameterId,
        selectionEntityIds,
      };
      return action;
    }
    case "array": {
      const action: ArrayAction = {
        id,
        kind: "array",
        name: nextActionName(session, "Array"),
        parameterId,
        selectionEntityIds,
        columnSpacing: 100,
        rowSpacing: 0,
      };
      return action;
    }
    case "lookup": {
      const action: LookupAction = {
        id,
        kind: "lookup",
        name: nextActionName(session, "Lookup"),
        parameterId,
        selectionEntityIds,
      };
      return action;
    }
  }
}

/**
 * Build a successful commit payload that dispatches
 * `geometry/add-dynamic-action` and ends the session.
 */
function buildActionCommit(
  session: DraftSession,
  kind: BlockAuthoringActionKind,
): { nextSession: DraftSession | null; commit?: DraftCommitResult } {
  const blockName = session.options.blockName ?? "";
  const action = buildActionPayload(session, kind);
  if (!action) return { nextSession: null };
  const summaryKey = (
    {
      move: "summary.baction.move_created",
      scale: "summary.baction.scale_created",
      stretch: "summary.baction.stretch_created",
      "polar-stretch": "summary.baction.polar_stretch_created",
      rotation: "summary.baction.rotation_created",
      flip: "summary.baction.flip_created",
      array: "summary.baction.array_created",
      lookup: "summary.baction.lookup_created",
    } as const
  )[kind];
  return {
    nextSession: null,
    commit: {
      geometryCommand: {
        type: "geometry/add-dynamic-action",
        source: "scene",
        blockName,
        action,
      },
      summary: t(summaryKey, { name: action.name }),
      commandText: "BACTION",
    },
  };
}

/**
 * Map an action kind from the palette id back to the
 * BlockAuthoringActionKind union (palette uses dash-separated forms,
 * the schema uses the same — they match 1:1).
 */
function actionKindFromStep(step: DraftStep): BlockAuthoringActionKind | null {
  if (step.startsWith("action-move-")) return "move";
  if (step.startsWith("action-scale-")) return "scale";
  if (step.startsWith("action-stretch-")) return "stretch";
  if (step.startsWith("action-polar-stretch-")) return "polar-stretch";
  if (step.startsWith("action-rotation-")) return "rotation";
  if (step.startsWith("action-flip-")) return "flip";
  if (step.startsWith("action-array-")) return "array";
  if (step.startsWith("action-lookup-")) return "lookup";
  return null;
}

/**
 * Advance a block-authoring placement session by one click.
 *
 * Returns `null` when `session.step` is not a block-authoring step — the
 * caller (advanceDraftSessionWithPoint) then falls through to its own
 * switch.
 *
 * `existingEntities` is consumed by the K.3 action select-objects steps
 * (see `findClosestEntityForActionSelect`). Defaults to an empty array;
 * unit tests for parameter placement (K.2) need not pass it.
 */
export function advanceBlockAuthoringWithPoint(
  session: DraftSession,
  point: DraftPoint,
  existingEntities: ReadonlyArray<EntityPreview> = [],
): { nextSession: DraftSession | null; commit?: DraftCommitResult } | null {
  switch (session.step) {
    // ── Linear parameter (Phase K.2a) ─────────────────────────────
    case "param-linear-base":
      return {
        nextSession: {
          ...session,
          step: "param-linear-end",
          points: [point],
          hoverPoint: null,
        },
      };

    case "param-linear-end":
      return {
        nextSession: {
          ...session,
          step: "param-linear-label",
          points: [...session.points, point],
          hoverPoint: null,
        },
      };

    case "param-linear-label": {
      const basePoint = session.points[0];
      const secondPoint = session.points[1];
      if (!basePoint || !secondPoint) {
        return { nextSession: null };
      }
      const blockName = session.options.blockName ?? "";
      const distance = distanceBetween(basePoint, secondPoint);
      const labelPosition: DraftPoint = point;

      const parameter: LinearParameter = {
        id: nextParameterId(session),
        kind: "linear",
        name: nextParameterName(session, "Distance"),
        gripPosition: { x: labelPosition.x, y: labelPosition.y },
        label: "",
        description: "",
        chainActions: false,
        showGrip: true,
        basePoint: { x: basePoint.x, y: basePoint.y },
        secondPoint: { x: secondPoint.x, y: secondPoint.y },
        defaultDistance: distance,
        currentDistance: distance,
        valueSet: defaultValueSet(),
      };

      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/add-dynamic-parameter",
            source: "scene",
            blockName,
            parameter,
          },
          summary: t("summary.bparam.linear_created", {
            name: parameter.name,
          }),
          commandText: "BPARAMETER",
        },
      };
    }

    // ── POINT parameter (Phase K.2b) — single click ───────────────
    case "param-point-place": {
      const blockName = session.options.blockName ?? "";
      const parameter: PointParameter = {
        id: nextParameterId(session),
        kind: "point",
        name: nextParameterName(session, "Position"),
        label: "",
        description: "",
        chainActions: false,
        showGrip: true,
        basePoint: { x: point.x, y: point.y },
        currentPoint: { x: point.x, y: point.y },
        xValueSet: defaultValueSet(),
        yValueSet: defaultValueSet(),
      };
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/add-dynamic-parameter",
            source: "scene",
            blockName,
            parameter,
          },
          summary: t("summary.bparam.point_created", { name: parameter.name }),
          commandText: "BPARAMETER",
        },
      };
    }

    // ── BASEPOINT parameter (Phase K.2b) — single click ──────────
    case "param-basepoint-place": {
      const blockName = session.options.blockName ?? "";
      const parameter: BasePointParameter = {
        id: nextParameterId(session),
        kind: "basepoint",
        name: nextParameterName(session, "BasePoint"),
        label: "",
        description: "",
        chainActions: false,
        showGrip: true,
        position: { x: point.x, y: point.y },
      };
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/add-dynamic-parameter",
            source: "scene",
            blockName,
            parameter,
          },
          summary: t("summary.bparam.basepoint_created", {
            name: parameter.name,
          }),
          commandText: "BPARAMETER",
        },
      };
    }

    // ── VISIBILITY parameter (Phase K.2b) — single click ─────────
    case "param-visibility-place": {
      const blockName = session.options.blockName ?? "";
      const defaultStateId = "state-1";
      const parameter: VisibilityParameter = {
        id: nextParameterId(session),
        kind: "visibility",
        name: nextParameterName(session, "Visibility"),
        label: "",
        description: "",
        chainActions: false,
        showGrip: true,
        position: { x: point.x, y: point.y },
        states: [
          { id: defaultStateId, name: "State1", invisibleEntityIds: [] },
        ],
        defaultStateId,
        currentStateId: defaultStateId,
      };
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/add-dynamic-parameter",
            source: "scene",
            blockName,
            parameter,
          },
          summary: t("summary.bparam.visibility_created", {
            name: parameter.name,
          }),
          commandText: "BPARAMETER",
        },
      };
    }

    // ── LOOKUP parameter (Phase K.2b) — single click ─────────────
    // Lookup parameters require a backing LookupTable — the schema
    // demands a non-empty `lookupTableId`. We emit BOTH a
    // `geometry/create-lookup-table` (empty table) AND a
    // `geometry/add-dynamic-parameter` so the reducer pipeline lands a
    // valid cross-reference. The user then populates the table via the
    // BLOOKUPTABLE editor.
    case "param-lookup-place": {
      const blockName = session.options.blockName ?? "";
      const tableId = nextLookupTableId(session);
      const tableName = nextLookupTableName(session);
      const table: LookupTable = {
        id: tableId,
        name: tableName,
        inputColumns: [],
        rows: [],
      };
      const parameter: LookupParameter = {
        id: nextParameterId(session),
        kind: "lookup",
        name: nextParameterName(session, "Lookup"),
        label: "",
        description: "",
        chainActions: false,
        showGrip: true,
        position: { x: point.x, y: point.y },
        reverseLookup: false,
        lookupTableId: tableId,
      };
      const commands: EditorCommand[] = [
        {
          type: "geometry/create-lookup-table",
          source: "scene",
          blockName,
          table,
        },
        {
          type: "geometry/add-dynamic-parameter",
          source: "scene",
          blockName,
          parameter,
        },
      ];
      return {
        nextSession: null,
        commit: {
          geometryCommands: commands,
          summary: t("summary.bparam.lookup_created", { name: parameter.name }),
          commandText: "BPARAMETER",
        },
      };
    }

    // ── FLIP parameter (Phase K.2b) — 3 clicks ───────────────────
    case "param-flip-base":
      return {
        nextSession: {
          ...session,
          step: "param-flip-end",
          points: [point],
          hoverPoint: null,
        },
      };

    case "param-flip-end":
      return {
        nextSession: {
          ...session,
          step: "param-flip-label",
          points: [...session.points, point],
          hoverPoint: null,
        },
      };

    case "param-flip-label": {
      const axisStart = session.points[0];
      const axisEnd = session.points[1];
      if (!axisStart || !axisEnd) return { nextSession: null };
      const blockName = session.options.blockName ?? "";
      const parameter: FlipParameter = {
        id: nextParameterId(session),
        kind: "flip",
        name: nextParameterName(session, "Flip"),
        gripPosition: { x: point.x, y: point.y },
        label: "",
        description: "",
        chainActions: false,
        showGrip: true,
        axisStart: { x: axisStart.x, y: axisStart.y },
        axisEnd: { x: axisEnd.x, y: axisEnd.y },
        defaultValue: false,
        currentValue: false,
        flipLabel: "Flip",
        baseLabel: "Base",
      };
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/add-dynamic-parameter",
            source: "scene",
            blockName,
            parameter,
          },
          summary: t("summary.bparam.flip_created", { name: parameter.name }),
          commandText: "BPARAMETER",
        },
      };
    }

    // ── POLAR parameter (Phase K.2b) — 3 clicks ──────────────────
    case "param-polar-base":
      return {
        nextSession: {
          ...session,
          step: "param-polar-end",
          points: [point],
          hoverPoint: null,
        },
      };

    case "param-polar-end":
      return {
        nextSession: {
          ...session,
          step: "param-polar-label",
          points: [...session.points, point],
          hoverPoint: null,
        },
      };

    case "param-polar-label": {
      const basePoint = session.points[0];
      const secondPoint = session.points[1];
      if (!basePoint || !secondPoint) return { nextSession: null };
      const blockName = session.options.blockName ?? "";
      const distance = distanceBetween(basePoint, secondPoint);
      const angle = angleBetweenDeg(basePoint, secondPoint);
      const parameter: PolarParameter = {
        id: nextParameterId(session),
        kind: "polar",
        name: nextParameterName(session, "Polar"),
        gripPosition: { x: point.x, y: point.y },
        label: "",
        description: "",
        chainActions: false,
        showGrip: true,
        basePoint: { x: basePoint.x, y: basePoint.y },
        secondPoint: { x: secondPoint.x, y: secondPoint.y },
        defaultDistance: distance,
        defaultAngle: angle,
        currentDistance: distance,
        currentAngle: angle,
        distanceValueSet: defaultValueSet(),
        angleValueSet: defaultValueSet(),
      };
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/add-dynamic-parameter",
            source: "scene",
            blockName,
            parameter,
          },
          summary: t("summary.bparam.polar_created", { name: parameter.name }),
          commandText: "BPARAMETER",
        },
      };
    }

    // ── XY parameter (Phase K.2b) — 3 clicks ─────────────────────
    case "param-xy-base":
      return {
        nextSession: {
          ...session,
          step: "param-xy-end",
          points: [point],
          hoverPoint: null,
        },
      };

    case "param-xy-end":
      return {
        nextSession: {
          ...session,
          step: "param-xy-label",
          points: [...session.points, point],
          hoverPoint: null,
        },
      };

    case "param-xy-label": {
      const basePoint = session.points[0];
      const secondPoint = session.points[1];
      if (!basePoint || !secondPoint) return { nextSession: null };
      const blockName = session.options.blockName ?? "";
      const dx = secondPoint.x - basePoint.x;
      const dy = secondPoint.y - basePoint.y;
      const parameter: XYParameter = {
        id: nextParameterId(session),
        kind: "xy",
        name: nextParameterName(session, "Position"),
        gripPosition: { x: point.x, y: point.y },
        label: "",
        description: "",
        chainActions: false,
        showGrip: true,
        basePoint: { x: basePoint.x, y: basePoint.y },
        secondPoint: { x: secondPoint.x, y: secondPoint.y },
        defaultX: dx,
        defaultY: dy,
        currentX: dx,
        currentY: dy,
        xValueSet: defaultValueSet(),
        yValueSet: defaultValueSet(),
      };
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/add-dynamic-parameter",
            source: "scene",
            blockName,
            parameter,
          },
          summary: t("summary.bparam.xy_created", { name: parameter.name }),
          commandText: "BPARAMETER",
        },
      };
    }

    // ── ROTATION parameter (Phase K.2b) — 3 clicks ───────────────
    case "param-rotation-base":
      return {
        nextSession: {
          ...session,
          step: "param-rotation-radius-end",
          points: [point],
          hoverPoint: null,
        },
      };

    case "param-rotation-radius-end":
      return {
        nextSession: {
          ...session,
          step: "param-rotation-label",
          points: [...session.points, point],
          hoverPoint: null,
        },
      };

    case "param-rotation-label": {
      const basePoint = session.points[0];
      const radiusEnd = session.points[1];
      if (!basePoint || !radiusEnd) return { nextSession: null };
      const blockName = session.options.blockName ?? "";
      const radius = distanceBetween(basePoint, radiusEnd);
      const angle = angleBetweenDeg(basePoint, radiusEnd);
      const parameter: RotationParameter = {
        id: nextParameterId(session),
        kind: "rotation",
        name: nextParameterName(session, "Rotation"),
        gripPosition: { x: point.x, y: point.y },
        label: "",
        description: "",
        chainActions: false,
        showGrip: true,
        basePoint: { x: basePoint.x, y: basePoint.y },
        radius,
        defaultAngle: angle,
        currentAngle: angle,
        angleValueSet: defaultValueSet(),
      };
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/add-dynamic-parameter",
            source: "scene",
            blockName,
            parameter,
          },
          summary: t("summary.bparam.rotation_created", {
            name: parameter.name,
          }),
          commandText: "BPARAMETER",
        },
      };
    }

    // ── ALIGNMENT parameter (Phase K.2b) — 2 clicks ──────────────
    case "param-alignment-base":
      return {
        nextSession: {
          ...session,
          step: "param-alignment-end",
          points: [point],
          hoverPoint: null,
        },
      };

    case "param-alignment-end": {
      const position = session.points[0];
      if (!position) return { nextSession: null };
      const blockName = session.options.blockName ?? "";
      const angle = angleBetweenDeg(position, point);
      const parameter: AlignmentParameter = {
        id: nextParameterId(session),
        kind: "alignment",
        name: nextParameterName(session, "Alignment"),
        label: "",
        description: "",
        chainActions: false,
        showGrip: true,
        position: { x: position.x, y: position.y },
        defaultAngle: angle,
        currentAngle: angle,
        alignmentType: "perpendicular",
      };
      return {
        nextSession: null,
        commit: {
          geometryCommand: {
            type: "geometry/add-dynamic-parameter",
            source: "scene",
            blockName,
            parameter,
          },
          summary: t("summary.bparam.alignment_created", {
            name: parameter.name,
          }),
          commandText: "BPARAMETER",
        },
      };
    }

    // ─── Phase K.3 — Action placement ──────────────────────────────
    //
    // Pick-parameter step: hit-test the click against the serialised
    // parameter list (`session.options.parameterPicks`). If a parameter
    // is found AND its kind is compatible with the action, advance to
    // select-objects with the parameter id stashed on session.options.
    // Incompatible pick → return a feedOnly commit so the host shows the
    // toast; session stays on the same step.
    case "action-move-pick-parameter":
    case "action-scale-pick-parameter":
    case "action-stretch-pick-parameter":
    case "action-polar-stretch-pick-parameter":
    case "action-rotation-pick-parameter":
    case "action-flip-pick-parameter":
    case "action-array-pick-parameter":
    case "action-lookup-pick-parameter": {
      const kind = actionKindFromStep(session.step);
      if (!kind) return { nextSession: session };
      const params = parseParameterPicks(session.options.parameterPicks);
      const tolerance = (() => {
        const raw = session.options.parameterPickTolerance;
        if (!raw) return undefined;
        const parsed = Number.parseFloat(raw);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
      })();
      const picked = findParameterAtPoint(point, params, tolerance);
      if (!picked) {
        // No parameter near the click — stay on the same step.
        return { nextSession: session };
      }
      if (!isCompatibleParameter(kind, picked.kind)) {
        // Incompatible — emit a feedOnly commit so the host can toast
        // the user; do not advance.
        return {
          nextSession: session,
          commit: {
            summary: t("error.baction.incompatible_parameter", {
              paramKind: picked.kind,
              actionKind: kind,
            }),
            commandText: "BACTION",
            feedOnly: true,
          },
        };
      }
      return { nextSession: advanceFromPickParameter(session, kind, picked.id) };
    }

    // Select-objects step: hit-test the click; append the picked
    // entity id to the running selection set and stay on the step.
    // Enter (empty text) advances to the next step — handled in
    // `advanceBlockAuthoringWithText`.
    case "action-move-select-objects":
    case "action-scale-select-objects":
    case "action-stretch-select-objects":
    case "action-polar-stretch-select-objects":
    case "action-rotation-select-objects":
    case "action-flip-select-objects":
    case "action-array-select-objects":
    case "action-lookup-select-objects": {
      const picked = findClosestEntityForActionSelect(point, existingEntities);
      if (!picked) return { nextSession: session };
      return { nextSession: appendSelectionEntityId(session, picked.id) };
    }

    // Stretch / Polar Stretch crossing-frame: two clicks define the
    // diagonal corners of the AutoCAD-style crossing window. After the
    // second click, advance to the place step.
    case "action-stretch-frame-corner-1":
      return {
        nextSession: {
          ...session,
          step: "action-stretch-frame-corner-2",
          points: [point],
          hoverPoint: null,
        },
      };
    case "action-stretch-frame-corner-2":
      return {
        nextSession: {
          ...session,
          step: "action-stretch-place",
          points: [...session.points, point],
          hoverPoint: null,
        },
      };
    case "action-polar-stretch-frame-corner-1":
      return {
        nextSession: {
          ...session,
          step: "action-polar-stretch-frame-corner-2",
          points: [point],
          hoverPoint: null,
        },
      };
    case "action-polar-stretch-frame-corner-2":
      return {
        nextSession: {
          ...session,
          step: "action-polar-stretch-place",
          points: [...session.points, point],
          hoverPoint: null,
        },
      };

    // Place step: drop the action glyph and dispatch
    // `geometry/add-dynamic-action`. The clicked point is informational
    // only (we do not store it on the action — AutoCAD's action glyph
    // is a UI marker; the schema does not carry it).
    case "action-move-place":
      return buildActionCommit(session, "move");
    case "action-scale-place":
      return buildActionCommit(session, "scale");
    case "action-stretch-place":
      return buildActionCommit(session, "stretch");
    case "action-polar-stretch-place":
      return buildActionCommit(session, "polar-stretch");
    case "action-rotation-place":
      return buildActionCommit(session, "rotation");
    case "action-flip-place":
      return buildActionCommit(session, "flip");
    case "action-array-place":
      return buildActionCommit(session, "array");
    case "action-lookup-place":
      return buildActionCommit(session, "lookup");

    default:
      return null;
  }
}

/**
 * Handle text input (typically Enter / empty submit) for action
 * placement sessions. Returns `null` when the step is not an action
 * step — the caller falls through to its own text handler.
 *
 * The select-objects steps treat empty text as "I'm done picking" and
 * advance to the place step (or frame-corner-1 for stretch). All other
 * action steps return `null` so coordinate input still works (typed
 * `5,7` resolves through the host's recursive call to
 * `advanceBlockAuthoringWithPoint`).
 */
export function advanceBlockAuthoringWithText(
  session: DraftSession,
  rawText: string,
): { nextSession: DraftSession | null; commit?: DraftCommitResult } | null {
  const text = rawText.trim();
  if (!isActionSelectObjectsStep(session.step)) return null;
  // Empty Enter advances; non-empty text is left for the caller (the
  // host's text handler will route it through the parser /
  // advanceBlockAuthoringWithPoint when it parses to a point).
  if (text !== "") return null;
  const next = nextStepAfterSelectObjects(session.step);
  if (!next) return null;
  return {
    nextSession: {
      ...session,
      step: next,
      hoverPoint: null,
      // Reset points buffer so the next step starts fresh (frame-corner-1
      // and place rely on `session.points` storing only their own
      // captured points).
      points: [],
    },
  };
}
